import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import { awsUpload, getStorageLink } from '../../../utils/api_service/storage_api';
import { updateProfilePic } from '../../../utils/api_service/user_api';
import { IS_IOS } from '../../assessment/perform/assigned_assessments/assigned_assessments';
import { LIVE_CAMERA_MAX_BYTES } from '../constants';
import type { ILiveCameraModalProps, ProfileCaptureTab } from '../interfaces';

export interface IUseLiveCameraModal extends ILiveCameraModalProps {
  activeTab: ProfileCaptureTab;
  setActiveTab: (tab: ProfileCaptureTab) => void;
  capturedImage: string | null;
  isCapturing: boolean;
  isSaving: boolean;
  cameraError: string | null;
  isFlashActive: boolean;
  dragOver: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isIosInline: boolean;
  handleCloseModal: () => void;
  handleCapturePhoto: () => void;
  handleRetake: () => void;
  handleFileChange: (file: File) => void;
  handleInputChange: (file: File | undefined) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDragLeave: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleSavePicture: () => Promise<void>;
  startCamera: () => Promise<void>;
  switchTab: (tab: ProfileCaptureTab) => void;
}

export const useLiveCameraModal = (props: ILiveCameraModalProps): IUseLiveCameraModal => {
  const { isOpen = true, setProfileLink, setIsModalOpen, currentProfilePic, userName, userRole } = props;
  const [activeTab, setActiveTab] = useState<ProfileCaptureTab>('camera');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isFlashActive, setIsFlashActive] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCameraStream();
    setCameraError(null);
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => undefined);
      }
    } catch (err: unknown) {
      console.warn('Camera access error:', err);
      const name = err instanceof DOMException ? err.name : '';
      setCameraError(
        name === 'NotAllowedError'
          ? 'Camera permission denied. Please allow camera access in your browser or choose Upload Photo.'
          : 'Unable to access camera. Please check your webcam connection or choose Upload Photo.',
      );
    }
  }, [stopCameraStream]);

  useEffect(() => {
    if (isOpen && activeTab === 'camera' && !capturedImage) {
      void startCamera();
    } else {
      stopCameraStream();
    }
    return () => {
      stopCameraStream();
    };
  }, [isOpen, activeTab, capturedImage, startCamera, stopCameraStream]);

  const handleCloseModal = useCallback(() => {
    stopCameraStream();
    setIsModalOpen(false);
  }, [stopCameraStream, setIsModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCloseModal]);

  const handleCapturePhoto = (): void => {
    const video = videoRef.current;
    if (!video) return;
    setIsCapturing(true);
    setIsFlashActive(true);
    window.setTimeout(() => setIsFlashActive(false), 150);

    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            setCapturedBlob(blob);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            setCapturedImage(dataUrl);
            stopCameraStream();
          } else {
            toast.error('Could not capture photo. Please try again.');
          }
          setIsCapturing(false);
        },
        'image/jpeg',
        0.92,
      );
    } else {
      setIsCapturing(false);
    }
  };

  const handleRetake = (): void => {
    setCapturedImage(null);
    setCapturedBlob(null);
    if (activeTab === 'camera') {
      void startCamera();
    }
  };

  const handleFileChange = (file: File): void => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPEG, PNG, or WEBP)');
      return;
    }
    if (file.size > LIVE_CAMERA_MAX_BYTES) {
      toast.error('Image file must be under 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCapturedImage(result);
      setCapturedBlob(file);
      stopCameraStream();
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (file: File | undefined): void => {
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (event: React.DragEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      handleFileChange(event.dataTransfer.files[0]);
    }
  };

  const handleSavePicture = async (): Promise<void> => {
    if (!capturedBlob) {
      toast.error('No picture selected');
      return;
    }
    setIsSaving(true);
    const fileName = `profile-${Date.now()}.jpg`;
    const folderName = 'profile-pic';
    try {
      const storageResponse = await getStorageLink(fileName, folderName);
      if (!storageResponse || !storageResponse.url || !storageResponse.fileName) {
        throw new Error('Failed to get upload link');
      }
      const uploadResult = await awsUpload(storageResponse.url, capturedBlob);
      if (uploadResult.status !== 200 && uploadResult.status !== 204) {
        throw new Error(`Upload failed with status ${uploadResult.status}`);
      }
      const updateResponse = await updateProfilePic(storageResponse.fileName);
      let updatedPicUrl = '';
      if (
        updateResponse?.profilePic
        && updateResponse.profilePic !== 'null'
        && updateResponse.profilePic !== 'undefined'
      ) {
        updatedPicUrl = updateResponse.profilePic;
      } else if (capturedImage) {
        updatedPicUrl = capturedImage;
      }
      if (updatedPicUrl) {
        setProfileLink(updatedPicUrl);
        localStorage.setItem('profilePic', updatedPicUrl);
        window.dispatchEvent(
          new CustomEvent('profilePicUpdated', { detail: { profilePic: updatedPicUrl } }),
        );
        window.dispatchEvent(new Event('storage'));
      }
      toast.success('Profile picture updated successfully');
      handleCloseModal();
    } catch (error: unknown) {
      console.error('Failed to upload profile picture:', error);
      const message = error instanceof Error ? error.message : 'Failed to update profile picture. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const switchTab = (tab: ProfileCaptureTab): void => {
    setActiveTab(tab);
    setCapturedImage(null);
    setCapturedBlob(null);
  };

  return {
    isOpen,
    setProfileLink,
    setIsModalOpen,
    currentProfilePic,
    userName,
    userRole,
    activeTab,
    setActiveTab,
    capturedImage,
    isCapturing,
    isSaving,
    cameraError,
    isFlashActive,
    dragOver,
    videoRef,
    fileInputRef,
    isIosInline: IS_IOS,
    handleCloseModal,
    handleCapturePhoto,
    handleRetake,
    handleFileChange,
    handleInputChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleSavePicture,
    startCamera,
    switchTab,
  };
};
